/**
 * Member Filters Component
 * 
 * Inline horizontal filter bar for searching members.
 */

import * as React from "react";
import { Button } from "@/components/ui/button";
import { CalendarIcon, RotateCcw } from "lucide-react";
import { LocationSelect } from "@/components/ui/LocationSelect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { useMembersFilters } from "../hooks/useMembersFilters";

interface MemberFiltersProps {
  filters: ReturnType<typeof useMembersFilters>;
  loading: boolean;
  onSearch: () => void;
  onClear: () => void;
  onLocationChange: (province?: string, city?: string) => void;
  onDateRangeChange?: () => void;
  allowedProvinces?: string[];
  provinceLocked?: boolean;
  allowedCities?: string[];
  cityLocked?: boolean;
}

export function MemberFilters({
  filters,
  loading,
  onSearch,
  onClear,
  onLocationChange,
  onDateRangeChange,
  allowedProvinces,
  provinceLocked = false,
  allowedCities,
  cityLocked = false,
}: MemberFiltersProps) {
  const hasActiveFilters = filters.selectedProvince || filters.selectedCity || filters.tipeIar || filters.startDate || filters.endDate;

  return (
    <div className="flex flex-wrap items-end gap-2">
      {/* Location Filters */}
      <LocationSelect
        province={filters.selectedProvince}
        city={filters.selectedCity}
        onProvinceChange={(province) => {
          filters.setSelectedProvince(province);
          filters.setSelectedCity(undefined);
          filters.selectedProvinceRef.current = province;
          filters.selectedCityRef.current = undefined;
          const currentSearch = filters.searchQueryRef.current;
          filters.updateFiltersInUrl(currentSearch, province, undefined);
          onLocationChange(province, undefined);
        }}
        onCityChange={(city) => {
          const currentProvince = filters.selectedProvinceRef.current;
          filters.setSelectedCity(city);
          filters.selectedCityRef.current = city;
          const currentSearch = filters.searchQueryRef.current;
          filters.updateFiltersInUrl(currentSearch, currentProvince, city);
          onLocationChange(currentProvince, city);
        }}
        disabled={loading}
        showLabels={false}
        allowedProvinces={allowedProvinces}
        provinceLocked={provinceLocked}
        allowedCities={allowedCities}
        cityLocked={cityLocked}
      />

      {/* Tipe IAR */}
      <Select
        value={filters.tipeIar || "all"}
        onValueChange={(value) => {
          const tipeIarValue = value === "all" ? undefined : value;
          filters.setTipeIar(tipeIarValue);
          filters.tipeIarRef.current = tipeIarValue;
          const currentSearch = filters.searchQueryRef.current;
          const currentProvince = filters.selectedProvinceRef.current;
          const currentCity = filters.selectedCityRef.current;
          filters.updateFiltersInUrl(
            currentSearch,
            currentProvince,
            currentCity,
            filters.startDateRef.current,
            filters.endDateRef.current,
            filters.dateFieldRef.current,
            tipeIarValue
          );
          if (onDateRangeChange) onDateRangeChange();
        }}
        disabled={loading}
      >
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Tipe IAR" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua IAR</SelectItem>
          <SelectItem value="IAR REGULAR">IAR Regular</SelectItem>
          <SelectItem value="IAR ASING">IAR Asing</SelectItem>
        </SelectContent>
      </Select>

      {/* Date Field */}
      <Select
        value={filters.dateField}
        onValueChange={(value) => {
          const field = value as "tanggal_terbit" | "masa_laku" | "masa_laku_kta";
          filters.setDateField(field);
          const currentSearch = filters.searchQueryRef.current;
          const currentProvince = filters.selectedProvinceRef.current;
          const currentCity = filters.selectedCityRef.current;
          filters.updateFiltersInUrl(
            currentSearch,
            currentProvince,
            currentCity,
            filters.startDate,
            filters.endDate,
            field,
            filters.tipeIar
          );
          if (onDateRangeChange) onDateRangeChange();
        }}
        disabled={loading}
      >
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Tanggal" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="masa_laku">Masa Laku IAR</SelectItem>
          <SelectItem value="masa_laku_kta">Masa Laku KTA</SelectItem>
        </SelectContent>
      </Select>

      {/* Start Date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[130px] h-9 justify-start text-left font-normal text-xs",
              !filters.startDate && "text-muted-foreground"
            )}
            disabled={loading}
          >
            <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
            {filters.startDate ? format(filters.startDate, "dd/MM/yy", { locale: idLocale }) : "Dari"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={filters.startDate}
            onSelect={(date) => {
              filters.setStartDate(date);
              const currentSearch = filters.searchQueryRef.current;
              const currentProvince = filters.selectedProvinceRef.current;
              const currentCity = filters.selectedCityRef.current;
              filters.updateFiltersInUrl(
                currentSearch,
                currentProvince,
                currentCity,
                date,
                filters.endDate,
                filters.dateField,
                filters.tipeIar
              );
              if (onDateRangeChange) onDateRangeChange();
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {/* End Date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[130px] h-9 justify-start text-left font-normal text-xs",
              !filters.endDate && "text-muted-foreground"
            )}
            disabled={loading}
          >
            <CalendarIcon className="mr-1.5 h-3.5 w-3.5" />
            {filters.endDate ? format(filters.endDate, "dd/MM/yy", { locale: idLocale }) : "Sampai"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={filters.endDate}
            onSelect={(date) => {
              filters.setEndDate(date);
              const currentSearch = filters.searchQueryRef.current;
              const currentProvince = filters.selectedProvinceRef.current;
              const currentCity = filters.selectedCityRef.current;
              filters.updateFiltersInUrl(
                currentSearch,
                currentProvince,
                currentCity,
                filters.startDate,
                date,
                filters.dateField,
                filters.tipeIar
              );
              if (onDateRangeChange) onDateRangeChange();
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      {/* Reset */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          disabled={loading}
          className="h-9 px-2 text-muted-foreground hover:text-foreground"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" />
          Reset
        </Button>
      )}
    </div>
  );
}
